import { useFormContext } from "react-hook-form";

import { SubscriptionDefaultIcon } from "@jti/brand-icons/subscription-default";
import { type ApiMagentoPurchaseType } from "@jti/magento-types";
import { ConsumablesFormStepSection } from "@jti/ui/components/ConsumablesFormStepSection";
import { RadioCard } from "@jti/ui/components/RadioCard";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { MonthlyPurchaseButtonContent } from "@product-details/components/MonthlyPurchaseButtonContent";

import { type ConsumablesFormValues } from "../../ConsumablesForm.types";

import { ConsumablesPurchaseTypeSectionTitle } from "./ConsumablesPurchaseTypeSectionTitle";

export type OrderFrequencyGroupProps = {
  groupName: keyof ConsumablesFormValues;
  step: number;
  onChange: (value: ApiMagentoPurchaseType) => boolean;
  isOnetimePurchaseDisabled?: boolean;
  isSubscriptionUnavailable?: boolean;
  value: ApiMagentoPurchaseType;
};

export const ConsumablesPurchaseTypeSection = ({
  groupName,
  step,
  onChange,
  isOnetimePurchaseDisabled = false,
  isSubscriptionUnavailable = false,
  value,
}: OrderFrequencyGroupProps) => {
  const { __ } = useTranslations();

  const { register } = useFormContext<ConsumablesFormValues>();

  const handleFrequencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value;
    const isPurchaseTypeValid = selectedValue === "regular" || selectedValue === "subscription";

    if (!isPurchaseTypeValid) {
      event.preventDefault();
      console.error(
        'Invalid purchase type selected. Only "regular" or "subscription" is allowed. Current selection value:',
        selectedValue
      );
      return;
    }

    const isSuccess = onChange(selectedValue);

    if (!isSuccess) {
      event.preventDefault();
    }
  };

  const registerProps = register(groupName as string, { onChange: handleFrequencyChange });

  return (
    <ConsumablesFormStepSection
      title={<ConsumablesPurchaseTypeSectionTitle step={step} purchaseType={value} />}
      data-testid="frequency-options"
    >
      <RadioCard
        groupName={groupName as string}
        value="regular"
        title={__("consumablesForm.purchase_type.regular")}
        isDisabled={isOnetimePurchaseDisabled}
        {...registerProps}
      />
      <RadioCard
        groupName={groupName as string}
        value="subscription"
        title={__("consumablesForm.purchase_type.subscription")}
        icon={<SubscriptionDefaultIcon width="27px" height="27px" />}
        description={<MonthlyPurchaseButtonContent />}
        isUnavailable={isSubscriptionUnavailable}
        {...registerProps}
      />
    </ConsumablesFormStepSection>
  );
};
